import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  type ReactElement,
} from "react";
import { Router, useLocation } from "wouter";

import { SiteLayout, StatePanel } from "@/components/layout/site-layout";
import { resolveRoute, type ResolvedRoute } from "@/content/routes";
import { HeadManager } from "@/seo/head-manager";
import { getRouteMeta } from "@/seo/meta";
import { DemoStoreProvider } from "@/state/demo-store";

let homeModule: Promise<typeof import("@/pages/home-page")> | undefined;
let productsModule: Promise<typeof import("@/pages/products-page")> | undefined;
let businessModule:
  | Promise<typeof import("@/pages/business-content-pages")>
  | undefined;
let quoteModule: Promise<typeof import("@/pages/business-quote-page")> | undefined;
let commerceModule: Promise<typeof import("@/pages/commerce-pages")> | undefined;
let accountModule: Promise<typeof import("@/pages/account-pages")> | undefined;
let searchModule: Promise<typeof import("@/pages/search-page")> | undefined;
let contentModule: Promise<typeof import("@/pages/content-pages")> | undefined;

const loadHome = () => (homeModule ??= import("@/pages/home-page"));
const loadProducts = () => (productsModule ??= import("@/pages/products-page"));
const loadBusiness = () =>
  (businessModule ??= import("@/pages/business-content-pages"));
const loadQuote = () => (quoteModule ??= import("@/pages/business-quote-page"));
const loadCommerce = () => (commerceModule ??= import("@/pages/commerce-pages"));
const loadAccount = () => (accountModule ??= import("@/pages/account-pages"));
const loadSearch = () => (searchModule ??= import("@/pages/search-page"));
const loadContent = () => (contentModule ??= import("@/pages/content-pages"));

const HomePage = lazy(() =>
  loadHome().then((module) => ({ default: module.HomePage })),
);
const ProductsPage = lazy(() =>
  loadProducts().then((module) => ({ default: module.ProductsPage })),
);
const ProductDetailPage = lazy(() =>
  loadProducts().then((module) => ({ default: module.ProductDetailPage })),
);
const BusinessPage = lazy(() =>
  loadBusiness().then((module) => ({ default: module.BusinessPage })),
);
const IndustriesPage = lazy(() =>
  loadBusiness().then((module) => ({ default: module.IndustriesPage })),
);
const OemPage = lazy(() =>
  loadBusiness().then((module) => ({ default: module.OemPage })),
);
const RecyclingPage = lazy(() =>
  loadBusiness().then((module) => ({ default: module.RecyclingPage })),
);
const QuotePage = lazy(() =>
  loadQuote().then((module) => ({ default: module.QuotePage })),
);
const CartPage = lazy(() =>
  loadCommerce().then((module) => ({ default: module.CartPage })),
);
const CheckoutPage = lazy(() =>
  loadCommerce().then((module) => ({ default: module.CheckoutPage })),
);
const CheckoutResultPage = lazy(() =>
  loadCommerce().then((module) => ({ default: module.CheckoutResultPage })),
);
const AccountPage = lazy(() =>
  loadAccount().then((module) => ({ default: module.AccountPage })),
);
const SearchPage = lazy(() =>
  loadSearch().then((module) => ({ default: module.SearchPage })),
);
const ContentPages = {
  MaterialPage: lazy(() =>
    loadContent().then((module) => ({ default: module.MaterialPage })),
  ),
  CertificationsPage: lazy(() =>
    loadContent().then((module) => ({ default: module.CertificationsPage })),
  ),
  AboutPage: lazy(() =>
    loadContent().then((module) => ({ default: module.AboutPage })),
  ),
  KnowledgePage: lazy(() =>
    loadContent().then((module) => ({ default: module.KnowledgePage })),
  ),
  ArticlePage: lazy(() =>
    loadContent().then((module) => ({ default: module.ArticlePage })),
  ),
  FaqPage: lazy(() =>
    loadContent().then((module) => ({ default: module.FaqPage })),
  ),
  ContactPage: lazy(() =>
    loadContent().then((module) => ({ default: module.ContactPage })),
  ),
  PolicyPage: lazy(() =>
    loadContent().then((module) => ({ default: module.PolicyPage })),
  ),
  StatesPage: lazy(() =>
    loadContent().then((module) => ({ default: module.StatesPage })),
  ),
  NotFoundPage: lazy(() =>
    loadContent().then((module) => ({ default: module.NotFoundPage })),
  ),
};

export interface AppProps {
  ssrPath?: string;
  initialRoute?: InitialRoute;
}

export interface InitialRoute {
  path: string;
  element: ReactElement;
}

export async function loadInitialRoute(path: string): Promise<InitialRoute> {
  const route = resolveRoute(path);
  const props = { locale: route.locale };
  let element: ReactElement;

  switch (route.kind) {
    case "home": {
      const module = await loadHome();
      element = <module.HomePage {...props} />;
      break;
    }
    case "products": {
      const module = await loadProducts();
      element = <module.ProductsPage {...props} category={route.category} />;
      break;
    }
    case "product": {
      const module = await loadProducts();
      element = <module.ProductDetailPage {...props} slug={route.slug} />;
      break;
    }
    case "business": {
      const module = await loadBusiness();
      element = <module.BusinessPage {...props} />;
      break;
    }
    case "industries": {
      const module = await loadBusiness();
      element = <module.IndustriesPage {...props} />;
      break;
    }
    case "industry": {
      const module = await loadBusiness();
      element = <module.IndustriesPage {...props} slug={route.slug} />;
      break;
    }
    case "oem": {
      const module = await loadBusiness();
      element = <module.OemPage {...props} />;
      break;
    }
    case "recycling": {
      const module = await loadBusiness();
      element = <module.RecyclingPage {...props} />;
      break;
    }
    case "quote": {
      const module = await loadQuote();
      element = <module.QuotePage {...props} />;
      break;
    }
    case "cart": {
      const module = await loadCommerce();
      element = <module.CartPage {...props} />;
      break;
    }
    case "checkout": {
      const module = await loadCommerce();
      element = <module.CheckoutPage {...props} />;
      break;
    }
    case "checkout-result": {
      const module = await loadCommerce();
      element = <module.CheckoutResultPage {...props} />;
      break;
    }
    case "account": {
      const module = await loadAccount();
      element = (
        <module.AccountPage
          {...props}
          section={route.section}
          slug={route.slug}
        />
      );
      break;
    }
    case "search": {
      const module = await loadSearch();
      element = <module.SearchPage {...props} />;
      break;
    }
    default: {
      const module = await loadContent();
      switch (route.kind) {
        case "material":
          element = <module.MaterialPage {...props} slug={route.slug} />;
          break;
        case "certifications":
          element = <module.CertificationsPage {...props} />;
          break;
        case "about":
          element = <module.AboutPage {...props} section={route.section} />;
          break;
        case "knowledge":
          element = <module.KnowledgePage {...props} />;
          break;
        case "knowledge-category":
          element = <module.KnowledgePage {...props} category={route.category} />;
          break;
        case "article":
          element = <module.ArticlePage {...props} slug={route.slug} />;
          break;
        case "faq":
          element = <module.FaqPage {...props} />;
          break;
        case "contact":
          element = <module.ContactPage {...props} />;
          break;
        case "policy":
          element = <module.PolicyPage {...props} slug={route.slug} />;
          break;
        case "states":
          element = <module.StatesPage {...props} />;
          break;
        case "not-found":
          element = <module.NotFoundPage {...props} />;
          break;
      }
    }
  }

  return { path, element };
}

function RouteContent({
  route,
  initialRoute,
  location,
}: {
  route: ResolvedRoute;
  initialRoute?: InitialRoute;
  location: string;
}) {
  if (initialRoute?.path === location) return initialRoute.element;
  const props = { locale: route.locale };

  switch (route.kind) {
    case "home":
      return <HomePage {...props} />;
    case "products":
      return <ProductsPage {...props} category={route.category} />;
    case "product":
      return <ProductDetailPage {...props} slug={route.slug} />;
    case "business":
      return <BusinessPage {...props} />;
    case "industries":
      return <IndustriesPage {...props} />;
    case "industry":
      return <IndustriesPage {...props} slug={route.slug} />;
    case "oem":
      return <OemPage {...props} />;
    case "recycling":
      return <RecyclingPage {...props} />;
    case "quote":
      return <QuotePage {...props} />;
    case "material":
      return <ContentPages.MaterialPage {...props} slug={route.slug} />;
    case "certifications":
      return <ContentPages.CertificationsPage {...props} />;
    case "about":
      return <ContentPages.AboutPage {...props} section={route.section} />;
    case "knowledge":
      return <ContentPages.KnowledgePage {...props} />;
    case "knowledge-category":
      return <ContentPages.KnowledgePage {...props} category={route.category} />;
    case "article":
      return <ContentPages.ArticlePage {...props} slug={route.slug} />;
    case "faq":
      return <ContentPages.FaqPage {...props} />;
    case "contact":
      return <ContentPages.ContactPage {...props} />;
    case "search":
      return <SearchPage {...props} />;
    case "account":
      return <AccountPage {...props} section={route.section} slug={route.slug} />;
    case "cart":
      return <CartPage {...props} />;
    case "checkout":
      return <CheckoutPage {...props} />;
    case "checkout-result":
      return <CheckoutResultPage {...props} />;
    case "policy":
      return <ContentPages.PolicyPage {...props} slug={route.slug} />;
    case "states":
      return <ContentPages.StatesPage {...props} />;
    case "not-found":
      return <ContentPages.NotFoundPage {...props} />;
  }
}

function RoutedApplication({ initialRoute }: { initialRoute?: InitialRoute }) {
  const [location] = useLocation();
  const route = useMemo(() => resolveRoute(location), [location]);
  const meta = useMemo(() => getRouteMeta(route), [route]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <>
      <HeadManager meta={meta} />
      <SiteLayout locale={route.locale}>
        <Suspense
          fallback={
            <div className="site-container py-16">
              <StatePanel
                title={route.locale === "en" ? "Loading page…" : "正在載入頁面…"}
                description={
                  route.locale === "en"
                    ? "The route module is loading."
                    : "正在載入此頁所需的程式模組。"
                }
              />
            </div>
          }
        >
          <RouteContent
            route={route}
            initialRoute={initialRoute}
            location={location}
          />
        </Suspense>
      </SiteLayout>
    </>
  );
}

export function App({ ssrPath, initialRoute }: AppProps) {
  return (
    <Router ssrPath={ssrPath}>
      <DemoStoreProvider>
        <RoutedApplication initialRoute={initialRoute} />
      </DemoStoreProvider>
    </Router>
  );
}
